import React from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from '../../components/ui/alert';
import { 
  Loader2, 
  AlertTriangle,
  Lock,
  Shield,
  Info,
  ChevronLeft
} from 'lucide-react';

/**
 * Status Revert Dialog - Confirm status change with batch handling options
 */
export default function RevertStatusDialog({
  open,
  onOpenChange,
  pendingRevertDispatch,
  pendingRevertStatus,
  revertCheckResult,
  batchAction,
  setBatchAction,
  batchDeleteReason,
  setBatchDeleteReason,
  overrideReason,
  setOverrideReason,
  handleRevertConfirm,
  reverting
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-500" />
            Confirm Status Change
          </DialogTitle>
          <DialogDescription>
            {pendingRevertDispatch && (
              <>
                Reverting <strong>#{pendingRevertDispatch.dispatch_number}</strong> from{' '}
                <span className="font-medium">{pendingRevertDispatch.status?.replace('_', ' ')}</span> to{' '}
                <span className="font-medium">{pendingRevertStatus?.replace('_', ' ')}</span>
              </>
            )}
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-2">
          {/* Blockers */}
          {revertCheckResult?.blockers?.map((blocker, i) => (
            <Alert key={i} variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>
                {blocker.type === 'dispatched_status' && 'Order Dispatched'}
                {blocker.type === 'batches_in_use' && 'Batches In Use'}
              </AlertTitle>
              <AlertDescription>
                {blocker.message}
                {blocker.dispatches && (
                  <ul className="list-disc list-inside mt-1 text-xs">
                    {blocker.dispatches.map((d, j) => (
                      <li key={j}>{d}</li>
                    ))}
                  </ul>
                )}
              </AlertDescription>
            </Alert>
          ))}
          
          {/* Warnings */}
          {revertCheckResult?.warnings?.map((warning, i) => (
            <Alert key={i} className="border-amber-200 bg-amber-50">
              <Info className="h-4 w-4 text-amber-600" />
              <AlertTitle className="text-amber-800">
                {warning.type === 'partial_batch_usage' && 'Partially Used Batches'}
                {warning.type === 'deletable_batches' && 'Batches Can Be Deleted'}
              </AlertTitle>
              <AlertDescription className="text-amber-700">
                {warning.message}
                {warning.batches && (
                  <div className="mt-1 text-xs">
                    Batches: {warning.batches.join(', ')}
                  </div>
                )}
              </AlertDescription>
            </Alert>
          ))}
          
          {/* Batch Decision - ALWAYS show when reverting status (user requirement) */}
          {(revertCheckResult?.requires_batch_decision || revertCheckResult?.batches_affected?.length > 0) && (
            <div className="space-y-3 p-4 border rounded-lg bg-red-50 border-red-200">
              <Label className="font-medium text-red-800">What should happen to the batches created for this order?</Label>
              
              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <input
                    type="radio"
                    id="batch-delete"
                    name="batch-action"
                    value="delete"
                    checked={batchAction === 'delete'}
                    onChange={(e) => setBatchAction(e.target.value)}
                    className="h-4 w-4 text-red-600"
                    disabled={revertCheckResult?.batches_affected?.every(b => !b.can_delete)}
                  />
                  <label 
                    htmlFor="batch-delete" 
                    className={`text-sm ${revertCheckResult?.batches_affected?.every(b => !b.can_delete) ? 'text-muted-foreground' : ''}`}
                  >
                    <span className="font-medium text-red-700">Delete Batches (Recommended)</span>
                    <span className="text-red-600 ml-1">- Permanently remove batches from the system</span>
                  </label>
                </div>
                
                <div className="flex items-center space-x-2">
                  <input
                    type="radio"
                    id="batch-keep"
                    name="batch-action"
                    value="keep"
                    checked={batchAction === 'keep'}
                    onChange={(e) => setBatchAction(e.target.value)}
                    className="h-4 w-4"
                  />
                  <label htmlFor="batch-keep" className="text-sm">
                    <span className="font-medium">Keep Batches</span>
                    <span className="text-muted-foreground ml-1">- Unlink from order but keep in inventory</span>
                  </label>
                </div>
              </div>
              
              {batchAction === 'delete' && (
                <div className="space-y-2 mt-3">
                  <Label className="text-sm">Reason for deletion</Label>
                  <Input
                    value={batchDeleteReason}
                    onChange={(e) => setBatchDeleteReason(e.target.value)}
                    placeholder="Order status reverted"
                  />
                </div>
              )}
            </div>
          )}
          
          {/* No batches message */}
          {!revertCheckResult?.batches_affected?.length && !revertCheckResult?.requires_batch_decision && (
            <div className="p-4 border rounded-lg bg-blue-50 border-blue-200">
              <p className="text-sm text-blue-700">
                <strong>Note:</strong> No batches are linked to this order. Status will be reverted directly.
              </p>
            </div>
          )}
          
          {/* Override Reason */}
          {revertCheckResult?.requires_override && revertCheckResult?.user_can_override && (
            <div className="space-y-2 p-4 border border-amber-300 rounded-lg bg-amber-50">
              <div className="flex items-center gap-2">
                <Shield className="w-4 h-4 text-amber-600" />
                <Label className="font-medium text-amber-800">Override Required</Label>
              </div>
              <p className="text-sm text-amber-700">
                You have permission to override this restriction. Please provide a reason.
              </p>
              <Textarea
                value={overrideReason}
                onChange={(e) => setOverrideReason(e.target.value)}
                placeholder="Reason for override..."
                rows={2}
                className="border-amber-300"
              />
            </div>
          )}
          
          {/* No permission to override */}
          {revertCheckResult?.requires_override && !revertCheckResult?.user_can_override && (
            <Alert variant="destructive">
              <Lock className="h-4 w-4" />
              <AlertTitle>Permission Required</AlertTitle>
              <AlertDescription>
                You do not have permission to override this restriction. 
                Contact a super admin or user with &quot;Override Order Protection&quot; permission.
              </AlertDescription>
            </Alert>
          )}
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          {(revertCheckResult?.can_change || revertCheckResult?.can_change_with_override) && (
            <Button 
              onClick={handleRevertConfirm} 
              disabled={reverting || (revertCheckResult?.requires_override && !revertCheckResult?.user_can_override)}
              variant={revertCheckResult?.requires_override ? "destructive" : "default"}
            >
              {reverting ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : revertCheckResult?.requires_override ? (
                <Shield className="w-4 h-4 mr-2" />
              ) : (
                <ChevronLeft className="w-4 h-4 mr-2" />
              )}
              {revertCheckResult?.requires_override ? 'Override & Revert' : 'Confirm Revert'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
